import { useStructureContext } from "../../../context/StructureContext";
import { useTranslation } from "react-i18next";
import { stdObject } from "../../../data/types/stdObject";
import { toPrice } from "../../../utils/toPrice";
import {
  headerCash,
  headerDocItems,
  headerOthersInterv,
} from "../../../data/admin";
import StdTable from "./StdTable";
import convertDate from "../../../utils/convertDate";
import { getCurrentDate } from "../../../utils/getCurrentDate";
import { colors } from "../../../data/colors";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

export default function SalesReport() {
  const { t } = useTranslation();
  const user: any = useSelector((state: RootState) => state.data.user);
  const { type, searchValues, section, printArea, headerInfo } =
    useStructureContext();
  const schedulesList: Array<stdObject> = useSelector(
    (state: RootState) => state.data.hor,
  );
  const salesSection = searchValues[section.id];

  const preCashTotal =
    "header" in salesSection
      ? salesSection.results.cash
          .filter((e: stdObject) => e.TYPE !== "COYP")
          .reduce((acc: number, curr: stdObject) => (acc += curr.TOT), 0)
      : 0;

  const cashTotals =
    "header" in salesSection
      ? salesSection.results?.totals?.TPA0
        ? [
            {
              NOMCAJ: t("p.invoicedAmount"),
              TOT: salesSection.results.totals.TPA0,
            },
            ...salesSection.results.cash,
          ]
        : salesSection.results.cash
      : [];

  const totalComm =
    "header" in salesSection
      ? salesSection.results.commissions.reduce(
          (acc: number, curr: stdObject) => (acc += Number(curr.PAGCOYP)),
          0,
        )
      : 0;

  const writePrintLabel = (e: stdObject) => {
    switch (e.name) {
      case "printDate":
        return convertDate({ info: getCurrentDate(true), t });
      case "shift":
        const shiftValue = salesSection.SCHIRE.value;
        const shiftResults = salesSection.results;
        const shiftTitle =
          shiftValue === ""
            ? t("p.fullShift")
            : schedulesList.find(
                (v: stdObject) => v.CODHOR === Number(shiftValue),
              )?.NOMHOR;
        return user.IRESUC
          ? `${shiftTitle}: ${convertDate({ info: shiftResults.date, t })} (${shiftResults.hours[0]} - ${shiftResults.hours[1]})`
          : `${shiftTitle}: ${convertDate({ info: shiftResults.date[0], t })}${shiftResults.date[1] !== "" ? " - " + convertDate({ info: shiftResults.date[1], t }) : ""}`;
      default:
        return e.name;
    }
  };

  const outstandingBalance =
    "header" in salesSection
      ? salesSection.results.previousBalance +
        (salesSection.results.totals?.TOTAL ?? 0)
      : 0;

  const finalBalance =
    "header" in salesSection
      ? salesSection.results.previousBalance +
        (salesSection.results.totals?.TOTAL ?? 0) -
        salesSection.results.totals.payTotal
      : 0;

  return "header" in salesSection ? (
    <div ref={printArea} className="listBox">
      {headerInfo.map((e: stdObject, index: number) => (
        <div key={index} style={e.style}>
          {e.label ? `${e.label}: ` : ""}
          {writePrintLabel(e)}
        </div>
      ))}
      <StdTable
        type={type}
        style={{ width: "100%", marginBottom: 20 }}
        header={salesSection.header}
        data={salesSection.results.report}
        totals={salesSection.results.totals}
      />
      <table
        className="standardTable"
        cellPadding={5}
        style={{ marginTop: 20 }}
        align="center"
      >
        <tbody>
          <tr style={{ fontWeight: "bolder" }}>
            <td>{t("p.outstandingBalance")}</td>
            <td style={{ textAlign: "right" }}>
              {toPrice(outstandingBalance)}
            </td>
          </tr>
          {salesSection.results.paymentList.map(
            (e: stdObject, index: number) => (
              <tr key={index}>
                <td>{e.label}</td>
                <td align="right">
                  {toPrice(salesSection.results.totals[e.code])}
                </td>
              </tr>
            ),
          )}
          <tr style={{ fontWeight: "bolder" }}>
            <td>{t("p.totalCharged")}</td>
            <td align="right">
              {toPrice(salesSection.results.totals.payTotal)}
            </td>
          </tr>
          <tr>
            <td>{t("p.finalBalance")}</td>
            <td align="right">{toPrice(finalBalance)}</td>
          </tr>
        </tbody>
      </table>

      {salesSection.results.docs.length > 0 && (
        <div
          className="colorHeader"
          style={{ backgroundColor: colors[type], marginTop: 20 }}
        >
          {t("p.salesDetails")}
        </div>
      )}
      {salesSection.results.docs.length > 0 &&
        salesSection.results.docs.map((e: stdObject, index: number) => (
          <StdTable
            key={index}
            type={"grey"}
            align="center"
            mainHeader={`${t(`c.${e.DOC}`)} ${e.SER}-${e.COD} (${convertDate({
              info: e.FEC,
              t,
            })}) (${e.CLI}) ${e.NOM} / ${t("c.AGE")}: ${e.ANO} / ${t("c.REC2")}: ${e.RNO}`}
            headerColor={colors.black}
            style={{ width: "100%" }}
            header={headerDocItems}
            data={e.items}
            totals={{ TOT: toPrice(e.NET) }}
          />
        ))}

      <StdTable
        type={type}
        align="center"
        mainHeader={t("p.commissions")}
        style={{ marginTop: 20 }}
        header={[
          { code: "FECCOYP", label: "p.date", type: "date" },
          { code: "NOMCOYP", label: "p.staff" },
          { code: "TOTCOYP", label: "p.total", align: "right" },
          ...salesSection.results.paymentCommList,
          { code: "PAGCOYP", label: "p.paid", align: "right" },
        ]}
        data={salesSection.results.commissions.map((e: stdObject) => {
          salesSection.results.paymentCommList.forEach((v: stdObject) => {
            if (typeof e[v.code] === "number") {
              e[v.code] = toPrice(e[v.code]);
            }
          });
          return e;
        })}
        totals={{
          ...salesSection.results.paymentCommList.reduce(
            (acc: stdObject, curr: stdObject) => {
              acc[curr.code] = searchValues[
                section.id
              ].results.commissions.reduce(
                (acc2: number, curr2: stdObject) =>
                  curr2[curr.code] ? (acc2 += Number(curr2[curr.code])) : acc2,
                0,
              );
              return acc;
            },
            {},
          ),
          TOTCOYP: toPrice(
            salesSection.results.commissions.reduce(
              (acc: number, curr: stdObject) => (acc += Number(curr.TOTCOYP)),
              0,
            ),
          ),
          PAGCOYP: toPrice(totalComm),
        }}
      />

      <StdTable
        type={type}
        align="center"
        mainHeader={t("p.items")}
        style={{ marginTop: 20 }}
        header={headerOthersInterv}
        data={salesSection.results.items}
        totals={{
          TOT: toPrice(
            salesSection.results.items.reduce(
              (acc: number, curr: stdObject) => (acc += curr.TOT),
              0,
            ),
          ),
        }}
      />

      <StdTable
        type={type}
        align="center"
        mainHeader={t("p.pettyCash")}
        style={{ marginTop: 20 }}
        header={headerCash}
        data={cashTotals}
        totals={{
          TOT: toPrice(
            cashTotals.reduce(
              (acc: number, curr: stdObject) => (acc += curr.TOT),
              0,
            ),
          ),
        }}
      />

      <StdTable
        type={type}
        align="center"
        mainHeader={t("p.summary")}
        style={{ marginTop: 20, marginBottom: 20 }}
        header={[
          { code: "NAME", label: "p.description" },
          { code: "TOTAL", label: "p.total", align: "right" },
        ]}
        data={[
          {
            NAME: t("p.totalCharged"),
            TOTAL: toPrice(salesSection.results.totals.payTotal),
          },
          {
            NAME: t("p.commissions"),
            TOTAL: toPrice(-totalComm),
          },
          {
            NAME: t("p.pettyCash"),
            TOTAL: toPrice(preCashTotal),
          },
        ]}
        totals={{
          TOTAL: toPrice(
            salesSection.results.totals.payTotal - totalComm + preCashTotal,
          ),
        }}
      />
    </div>
  ) : (
    <></>
  );
}
